import { AxiosInstance } from "axios"
import { GenericResponse } from "."
import { UserModel } from "./user.api"

export interface ShippingRateModel {
    id: number
    shipping: string
    service: string
    dest: string
    weight: number
    base: number
    surcharge: number
    total_rate: number
    new_rate: number
    updated_by: UserModel
    updated_at: string
}

export interface CreateShippingRateRequest {}

export interface UpdateShippingRateRequest {
    id?: number
    margin?: number
}

export interface SurchargeModel {
    id: number
    updated_by: UserModel
    created_by: UserModel
    updated_at: string
}

export interface ValueAddedServiceModel {
    id: number
    service: string
    service_value: number
    service_type: string
    category: string
    warehouse_country_code: string
    updated_by: UserModel
    created_by: UserModel
    updated_at: string
}

export interface UpdateValueAddedServiceRequest {
    id?: number
    service_value?: number
    service_type?: string
}

export interface CreateValueAddedServiceRequest {
    service_value?: number
    service_type?: string
    description?: string
    service?: string
    warehouse_id?: number
    is_external?: number | boolean
    min_value?: number
    category?: string
}

export interface CreateSurchargeRequest {
    name: string
    default_value: number
    type: string
    shipping_service_id: number
    is_main: boolean | number
}

export interface UpdateSurchargeRequest {
    id: number
    name?: string
    default_value?: number
    type?: string
    shipping_service_id?: number
    is_main?: boolean | number
}

export interface BulkUpdateShippingRateRequest {
    action: string
    action_type: string
    base_charge_amount: number
    ids: number[]
    surcharges: Record<string, any>[]
    filters?: {
        warehouse_id: number
        country_code: string
        shipping_company_id: number
        shipping_service: string
    }
}

const pricingApi = (apiInstance: AxiosInstance) => {
    const shippingRatesList = async ({
        warehouse,
        country,
        shippingCompany,
        shippingService
    }: {
        warehouse?: string
        country?: string
        shippingCompany?: number | string
        shippingService?: string
    }): Promise<GenericResponse> => {
        const filterByCountry = country && country !== "" ? "&country_code=" + country : ""
        const filterByShippingCompany =
            shippingCompany && shippingCompany !== "" ? "&shipping_company_id=" + shippingCompany : ""
        const filterByShippingService =
            shippingService && shippingService !== "" ? "&shipping_service=" + shippingService : ""

        const res: Promise<GenericResponse> = apiInstance.get(
            `/rates/v1?warehouse_id=${warehouse}` +
                filterByCountry +
                filterByShippingCompany +
                filterByShippingService
        )
        return res
    }

    const updateShippingRate = async (
        existingShippingRateData: UpdateShippingRateRequest,
        warehouse: string
    ): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.put(
            `/rates/v1?rate_id=${existingShippingRateData?.id}&warehouse_id=${warehouse}`,
            existingShippingRateData
        )
        return res
    }

    const listValueAddedServices = async ({ warehouse }: { warehouse: string }): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get(`/services/v1?warehouse_id=${warehouse}`)
        return res
    }

    const createValueAddedService = async (
        newValueAddedServiceData: CreateValueAddedServiceRequest
    ): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/services/v1`, newValueAddedServiceData)
        return res
    }

    const updateValueAddedService = async (
        existingValueAddedServiceData: UpdateValueAddedServiceRequest,
        warehouse: string
    ): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.put(
            `/services/v1?service_id=${existingValueAddedServiceData?.id}&warehouse_id=${warehouse}`,
            existingValueAddedServiceData
        )
        return res
    }

    const deleteValueAddedService = async (valueAddedServiceId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.delete(`/services/v1/${valueAddedServiceId}`)
        return res
    }

    const listShippingRates = async (customerId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get(`/rate/discount/v1?user_id=${customerId}`)
        return res
    }

    const listCustomerValueAddedServices = async (customerId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get(`/user/services/v1?user_id=${customerId}`)
        return res
    }

    // const listRateSlabs = async (): Promise<GenericResponse> => {
    //     const res: Promise<GenericResponse> = apiInstance.get(
    //         `/rate-slabs/v1`
    //     )
    //     return res
    // }

    const createOrUpdateShippingRate = async (rateData: Record<string, any>): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/rate/discount/v1`, rateData)
        return res
    }

    const listShippingServices = async (shippingRateWarehouse: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get(
            `/shipping-services/v1?warehouse_id=${shippingRateWarehouse}`
        )
        return res
    }

    const deleteShippingRate = async (shippingRateId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.delete(
            `/rate/discount/v1?user_rate_discount_id=${shippingRateId}`
        )
        return res
    }

    const createOrUpdateVAS = async (serviceData: Record<string, any>): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/user/services/v1`, serviceData)
        return res
    }

    const deleteVAS = async (vasId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.delete(
            `/user/services/v1?user_value_added_service_id=${vasId}`
        )
        return res
    }

    const bulkUploadUpdate = async (rates: any, warehouseId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(
            `/bulk/rates/v1?warehouse_id=${warehouseId}`,
            rates,
            {
                headers: {
                    "content-type": rates?.type
                }
            }
        )
        return res
    }

    const listSurcharges = async (
        shippingCompanyId?: number,
        shippingServiceId?: number
    ): Promise<GenericResponse> => {
        const queryParams =
            shippingCompanyId && shippingCompanyId !== 0 && shippingServiceId && shippingServiceId !== 0
                ? `?shipping_company_id=${shippingCompanyId}&shipping_service_id=${shippingServiceId}`
                : shippingCompanyId && shippingCompanyId !== 0
                  ? `?shipping_company_id=${shippingCompanyId}`
                  : shippingServiceId && shippingServiceId !== 0
                    ? `?shipping_service_id=${shippingServiceId}`
                    : ""
        const res: Promise<GenericResponse> = apiInstance.get(`/surcharge/v1${queryParams}`)
        return res
    }

    const createSurcharge = async (surchargeData: CreateSurchargeRequest): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/surcharge/v1`, surchargeData)
        return res
    }

    const updateSurcharge = async (existingSurchargeData: UpdateSurchargeRequest): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.put(
            `/surcharge/v1/${existingSurchargeData?.id}`,
            existingSurchargeData
        )
        return res
    }

    const deleteSurcharge = async (surchargeId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.delete(`/surcharge/v1/${surchargeId}`)
        return res
    }

    const bulkUpdateShippingRate = async (
        bulkRatesData: BulkUpdateShippingRateRequest,
        type: string
    ): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/bulk-update/v1?type=${type}`, bulkRatesData)

        return res
    }

    return {
        shippingRatesList,
        updateShippingRate,
        updateValueAddedService,
        listShippingRates,
        listCustomerValueAddedServices,
        listValueAddedServices,
        deleteValueAddedService,
        // listRateSlabs,
        createOrUpdateShippingRate,
        listShippingServices,
        deleteShippingRate,
        createOrUpdateVAS,
        deleteVAS,
        bulkUploadUpdate,
        createValueAddedService,
        listSurcharges,
        createSurcharge,
        updateSurcharge,
        deleteSurcharge,
        bulkUpdateShippingRate
    }
}

export default pricingApi
